import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { Contractor } from '../../../shared/models/entity/contractor/contractor.model';
import { ContractorFilter } from '../../../shared/models/entity/contractor/contractor-filter.model';
import { ContractorsService } from '../../../shared/services/entity/contractors.service';

const MAX_RESULTS = 6;

/** Searches the Handwerker / Dienstleister by name — the trades show as the subtitle. */
@Injectable({ providedIn: 'root' })
export class ContractorsSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(ContractorsService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'contractors';

    readonly label = 'Dienstleister';

    readonly order = 23;

    readonly icon: Icon = ICON.toolbox;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // Contractors honor the Objekt scope (assignments there); other scopes skip them
        if (hasUnsupportedScope(scope, ['propertyId'])) {
            return of([]);
        }

        const propertyId = scope.context?.['propertyId'];

        return this.entityService
            .getAll({
                filter: {
                    filter: query,
                    propertyId: propertyId ? String(propertyId) : undefined,
                } as ContractorFilter,
                pagination: { page: 1, pageSize: MAX_RESULTS },
            })
            .pipe(map((contractors) => contractors.map((contractor) => this.toResultItem(contractor))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(contractor: Contractor): SearchResultItem {
        return {
            id: contractor.id,
            title: contractor.name,
            subtitle: [contractor.trades.join(', '), contractor.emergencyPhone ? `Notdienst ${contractor.emergencyPhone}` : null]
                .filter(Boolean).join(' · '),
            icon: this.icon,
            payload: contractor,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/contractors', contractor.id]);
                    },
                },
                {
                    id: 'scope',
                    label: 'Als Suchfilter verwenden',
                    icon: ICON.funnel,
                    keepOpen: true,
                    execute: (_, context) => {
                        context.setScope('contractorId', contractor.id, `Dienstleister: ${contractor.name}`);
                    },
                },
            ],
        };
    }
}
